import { Timestamp } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, startOfDay, subDays } from "date-fns";
import { ja } from "date-fns/locale";
import { useMemo, useState } from "react";
import ArrowDownward from "~icons/material-symbols/arrow-downward";
import ArrowUpward from "~icons/material-symbols/arrow-upward";
import Close from "~icons/material-symbols/close.svg";
import Reload from "~icons/material-symbols/refresh.svg";
import Search from "~icons/material-symbols/search.svg";
import { HokkaidoR8HouseholderCodeStatus } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  SearchHokkaidoR8HouseholderCodeStatusesRequest,
  SearchHokkaidoR8HouseholderCodeStatusesRequest_OrderBy as OrderBy,
  SearchHokkaidoR8HouseholderCodeStatusesRequest_SortDirection as SortDirection,
  type SearchHokkaidoR8HouseholderCodeStatusesResponse_Row as Row,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import { css } from "../../../../../styled-system/css";
import { DateTimeRangePicker } from "../../../../components/date-time-range-picker";
import { ScrollableDialog } from "../../../../components/dialog";
import { LoadingOverlay } from "../../../../components/loading-overlay";
import { useSearchHokkaidoR8HouseholderCodeStatuses } from "../../../../libs/admin/hooks";
import { formatGender } from "../../../../libs/utils/format-gender";
import { button } from "../../../../styles/buttonStyles";
import { contentWrapper } from "../../../../styles/contentWrapper";
import { input, inputButton, inputWithButtonContainer } from "../../../../styles/inputStyles";
import { pageTitle, titleWrapper } from "../../../../styles/sectionStyles";
import {
  table,
  tableCell,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from "../../../../styles/tableStyles";

export const Route = createFileRoute("/admin/hokkaido/code-statuses/")({
  component: RouteComponent,
});

const PAGE_SIZE = 50;

type Filters = {
  code: string;
  nameKana: string;
  status: HokkaidoR8HouseholderCodeStatus;
  usedStart: Date | null;
  usedEnd: Date | null;
};

const newEmptyFilters = (): Filters => ({
  code: "",
  nameKana: "",
  status: HokkaidoR8HouseholderCodeStatus.UNSPECIFIED,
  usedStart: null,
  usedEnd: null,
});

const formatStatus = (status: HokkaidoR8HouseholderCodeStatus): string => {
  switch (status) {
    case HokkaidoR8HouseholderCodeStatus.UNUSED:
      return "未受取";
    case HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE:
      return "ポイント受取済";
    case HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER:
      return "ギフト券交付済";
    default:
      return "不明";
  }
};

const errorMessage = (error: unknown): string => {
  if (error instanceof ConnectError) {
    if (error.code === Code.PermissionDenied) {
      return "権限がありません。組織を確認してください。";
    }
    if (error.code === Code.InvalidArgument) {
      return "検索条件が正しくありません。シリアルナンバーの形式を確認してください。";
    }
    return error.message;
  }
  return "通信に失敗しました。時間を置いて再度お試しください。";
};

function RouteComponent() {
  const [form, setForm] = useState<Filters>(newEmptyFilters());
  const [applied, setApplied] = useState<Filters | null>(null);
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState<OrderBy>(OrderBy.USED_AT);
  const [descending, setDescending] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [returnsTarget, setReturnsTarget] = useState<Row | null>(null);

  const nameKanaInvalid = form.nameKana !== "" && form.nameKana.length < 2;
  const canSubmit =
    !nameKanaInvalid &&
    (form.code.trim() !== "" ||
      form.nameKana.trim() !== "" ||
      form.status !== HokkaidoR8HouseholderCodeStatus.UNSPECIFIED ||
      form.usedStart !== null);

  const request = useMemo(() => {
    const req = new SearchHokkaidoR8HouseholderCodeStatusesRequest({
      page,
      pageSize: PAGE_SIZE,
      orderBy,
      sortDirection: descending
        ? SortDirection.DESC
        : SortDirection.ASC,
    });
    if (applied) {
      if (applied.code.trim() !== "")
        req.code = applied.code.trim();
      if (applied.nameKana.trim() !== "") req.nameKana = applied.nameKana.trim();
      if (applied.status !== HokkaidoR8HouseholderCodeStatus.UNSPECIFIED)
        req.status = applied.status;
      if (applied.usedStart) req.usedAtStart = Timestamp.fromDate(applied.usedStart);
      if (applied.usedEnd) req.usedAtEnd = Timestamp.fromDate(applied.usedEnd);
    }
    return req;
  }, [applied, page, orderBy, descending]);

  const { data, isFetching, error } = useSearchHokkaidoR8HouseholderCodeStatuses(
    request,
    applied !== null,
  );

  const handleSubmit = () => {
    if (!canSubmit) return;
    setPage(1);
    setApplied(form);
  };

  const handleReset = () => {
    setForm(newEmptyFilters());
    setApplied(null);
    setPage(1);
    setOrderBy(OrderBy.USED_AT);
    setDescending(true);
    setIsDatePickerOpen(false);
  };

  const toggleSort = (key: OrderBy) => {
    setPage(1);
    if (key === orderBy) {
      setDescending((prev) => !prev);
      return;
    }
    setOrderBy(key);
    setDescending(key === OrderBy.USED_AT);
  };

  const rows = data?.rows ?? [];
  const totalCount = data ? Number(data.totalCount) : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>ポイント受取状況照会</h1>
      </div>
      <div className={css({ display: "flex", flexDirection: "column", gap: "16px" })}>
        <form
          className={css({ display: "flex", flexDirection: "column", gap: "12px" })}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          onReset={(e) => {
            e.preventDefault();
            handleReset();
          }}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "16px",
              alignItems: "end",
            })}
          >
            <Field label="シリアルナンバー" width="280px">
              <input
                type="text"
                placeholder="234ABCDEFGHJ"
                className={input()}
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </Field>

            <Field label="カナ氏名（2文字以上・部分一致）" width="240px">
              <input
                type="text"
                placeholder="タナカ"
                className={input({ semantic: nameKanaInvalid ? "invalid" : undefined })}
                value={form.nameKana}
                onChange={(e) => setForm({ ...form, nameKana: e.target.value })}
              />
            </Field>

            <Field label="受取状況" width="180px">
              <select
                className={css(input.raw(), { appearance: "auto" })}
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: Number(e.target.value) as HokkaidoR8HouseholderCodeStatus,
                  })
                }
              >
                <option value={HokkaidoR8HouseholderCodeStatus.UNSPECIFIED}>
                  すべて
                </option>
                <option value={HokkaidoR8HouseholderCodeStatus.UNUSED}>未受取</option>
                <option value={HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE}>
                  ポイント受取済
                </option>
                <option value={HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER}>
                  ギフト券交付済
                </option>
              </select>
            </Field>

            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                width: "360px",
                position: "relative",
              })}
            >
              <div className={css({ fontSize: "12px" })}>受取日時</div>
              <div className={inputWithButtonContainer()}>
                <button
                  type="button"
                  className={css(input.raw(), {
                    display: "flex",
                    alignItems: "center",
                    textAlign: "left",
                    cursor: "pointer",
                    pr: "40px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: form.usedStart ? "text.primary" : "text.placeholder",
                  })}
                  onClick={() => setIsDatePickerOpen((prev) => !prev)}
                >
                  {form.usedStart && form.usedEnd
                    ? `${format(form.usedStart, "yyyy/MM/dd HH:mm", { locale: ja })} 〜 ${format(form.usedEnd, "yyyy/MM/dd HH:mm", { locale: ja })}`
                    : "指定なし"}
                </button>
                {form.usedStart && (
                  <button
                    type="button"
                    className={inputButton({ semantic: "danger" })}
                    onClick={() => setForm({ ...form, usedStart: null, usedEnd: null })}
                  >
                    <Close />
                  </button>
                )}
              </div>
              {isDatePickerOpen && (
                <>
                  <div
                    className={css({
                      position: "fixed",
                      inset: 0,
                      zIndex: 25,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                    })}
                    onClick={() => setIsDatePickerOpen(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setIsDatePickerOpen(false);
                    }}
                  />
                  <div
                    className={css({
                      position: "absolute",
                      top: "62px",
                      left: 0,
                      zIndex: 30,
                      bg: "white",
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: "border.secondary",
                      shadow: "md",
                      p: "12px",
                      maxWidth: "fit-content",
                      overflowX: "auto",
                    })}
                  >
                    <DateTimeRangePicker
                      limit={form.usedStart ?? startOfDay(subDays(new Date(), 7))}
                      offset={form.usedEnd ?? new Date()}
                      onSubmit={({ limit, offset }) => {
                        setForm({ ...form, usedStart: limit, usedEnd: offset });
                        setIsDatePickerOpen(false);
                      }}
                      onCancel={() => setIsDatePickerOpen(false)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {nameKanaInvalid && (
            <div className={css({ fontSize: "13px", color: "text.accentError" })}>
              カナ氏名は2文字以上で入力してください。
            </div>
          )}

          <div className={css({ display: "flex", gap: "12px" })}>
            <button type="reset" className={button()}>
              <Reload />
              リセット
            </button>
            <button type="submit" className={button({ color: "primary" })} disabled={!canSubmit}>
              <Search />
              検索
            </button>
          </div>

          <div className={css({ fontSize: "13px", color: "text.secondary" })}>
            条件は複数指定でき、すべてを満たす結果を表示します。シリアルナンバーは完全一致、カナ氏名は部分一致です。
          </div>
        </form>

        {error && (
          <div className={css({ fontSize: "14px", color: "text.accentError" })}>
            {errorMessage(error)}
          </div>
        )}

        {applied && !error && (
          <div
            className={css({
              fontSize: "14px",
              color: "text.secondary",
            })}
          >
            検索結果: {totalCount} 件
          </div>
        )}

        <div className={tableWrapper()}>
          {isFetching && <LoadingOverlay />}
          <table className={table()}>
            <thead className={tableHead({ disabled: rows.length === 0 })}>
              <tr>
                <SortableTh
                  label="シリアルナンバー"
                  sortKey={OrderBy.CODE}
                  activeKey={orderBy}
                  descending={descending}
                  onClick={toggleSort}
                />
                <th className={tableHeadCell()}>受取状況</th>
                <th className={tableHeadCell()}>返戻</th>
                <SortableTh
                  label="受取日時"
                  sortKey={OrderBy.USED_AT}
                  activeKey={orderBy}
                  descending={descending}
                  onClick={toggleSort}
                />
                <th className={tableHeadCell()}>登録者</th>
                <SortableTh
                  label="カナ氏名"
                  sortKey={OrderBy.NAME_KANA}
                  activeKey={orderBy}
                  descending={descending}
                  onClick={toggleSort}
                />
                <th className={tableHeadCell()}>性別</th>
                <th className={tableHeadCell()}>生年月日</th>
              </tr>
            </thead>
            {rows.map((row) => (
              <tbody key={row.code}>
                <tr className={tableRow()}>
                  <td className={tableCell()}>{row.code}</td>
                  <td className={tableCell()}>{formatStatus(row.status)}</td>
                  <td className={tableCell()}>
                    {row.postalReturns.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setReturnsTarget(row)}
                        className={css({
                          display: "inline-flex",
                          alignItems: "center",
                          cursor: "pointer",
                          background: "none",
                          border: "none",
                          font: "inherit",
                          p: 0,
                          color: "text.accentError",
                          fontWeight: "700",
                          textDecoration: "underline",
                          _hover: { opacity: 0.8 },
                        })}
                      >
                        返戻 {row.postalReturns.length}件
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className={tableCell()}>
                    {row.usedAt
                      ? format(row.usedAt.toDate(), "yyyy/MM/dd HH:mm:ss", { locale: ja })
                      : "-"}
                  </td>
                  <td className={tableCell()}>
                    {row.subscriptionId ? (
                      <Link
                        to="/admin/users/$subscriptionOrUserId"
                        params={{ subscriptionOrUserId: row.subscriptionId }}
                        className={css({
                          color: "text.accentPrimary",
                          textDecoration: "underline",
                          textUnderlineOffset: "2px",
                          _hover: { opacity: 0.8 },
                        })}
                      >
                        {row.subscriptionId}
                      </Link>
                    ) : (
                      (row.staffId ?? "-")
                    )}
                  </td>
                  <td className={tableCell()}>{row.nameKana}</td>
                  <td className={tableCell()}>{formatGender(row.gender)}</td>
                  <td className={tableCell()}>
                    {row.birthday
                      ? format(row.birthday.toDate(), "yyyy/MM/dd", { locale: ja })
                      : "-"}
                  </td>
                </tr>
              </tbody>
            ))}
          </table>
        </div>

        {applied && totalCount > 0 && (
          <div
            className={css({
              display: "flex",
              gap: "12px",
              alignItems: "center",
              justifyContent: "flex-end",
            })}
          >
            <button
              type="button"
              className={button()}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            >
              前へ
            </button>
            <span className={css({ fontSize: "14px", color: "text.secondary" })}>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className={button()}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isFetching}
            >
              次へ
            </button>
          </div>
        )}
      </div>

      <ScrollableDialog
        open={returnsTarget !== null}
        onClose={() => setReturnsTarget(null)}
        title="返戻履歴"
      >
        {returnsTarget && (
          <div className={css({ display: "flex", flexDirection: "column", gap: "16px" })}>
            <div className={css({ fontSize: "14px", color: "text.secondary" })}>
              {returnsTarget.code}（{returnsTarget.nameKana}）
            </div>
            <div className={css({ display: "flex", flexDirection: "column", gap: "12px" })}>
              {returnsTarget.postalReturns.map((r, i) => (
                <div
                  key={`${r.returnedAt?.toJsonString() ?? i}`}
                  className={css({
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    p: "12px",
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "border.secondary",
                  })}
                >
                  <div className={css({ fontSize: "14px", fontWeight: "700" })}>
                    {r.returnedAt
                      ? format(r.returnedAt.toDate(), "yyyy/MM/dd HH:mm:ss", { locale: ja })
                      : "-"}
                  </div>
                  <div className={css({ fontSize: "14px" })}>理由: {r.reason}</div>
                  <div className={css({ fontSize: "13px", color: "text.secondary" })}>
                    担当者: {r.staffId}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </ScrollableDialog>
    </div>
  );
}

function Field(props: {
  label: string;
  width: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        width: props.width,
      })}
    >
      <div className={css({ fontSize: "12px" })}>{props.label}</div>
      {props.children}
    </div>
  );
}

function SortableTh(props: {
  label: string;
  sortKey: OrderBy;
  activeKey: OrderBy;
  descending: boolean;
  onClick: (key: OrderBy) => void;
}) {
  const active = props.sortKey === props.activeKey;
  // The default direction of the first click matches the usecase (used_at: desc, others: asc).
  const defaultDescending = props.sortKey === OrderBy.USED_AT;
  const descending = active ? props.descending : defaultDescending;
  const Arrow = descending ? ArrowDownward : ArrowUpward;
  return (
    <th
      className={tableHeadCell()}
      aria-sort={active ? (props.descending ? "descending" : "ascending") : "none"}
    >
      <button
        type="button"
        onClick={() => props.onClick(props.sortKey)}
        className={css({
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          cursor: "pointer",
          background: "none",
          border: "none",
          font: "inherit",
          p: 0,
          color: active ? "text.accentPrimary" : "inherit",
          fontWeight: active ? "700" : "inherit",
          _hover: { color: "text.accentPrimary", "& svg": { opacity: 1 } },
        })}
      >
        {props.label}
        <Arrow
          width="16"
          height="16"
          className={css({ opacity: active ? 1 : 0.3 })}
        />
      </button>
    </th>
  );
}
